import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@volca/ui/components/ui/dialog";
import { Stack } from "@volca/ui/components/ui/stack";
import type { DocumentExample } from "./types";
import { useStepDialog } from "./use-step-dialog";

// The examples are read, not collected: a customer wants to see what a good pay plan looks
// like without a file landing in their downloads folder and a text editor opening on top
// of the flow. Fetched from the same origin rather than duplicated here, so the file the
// download link hands over is the editable version of the file they were shown.
export const ExamplePreviewModal = ({
  example,
  onClose,
}: {
  example: DocumentExample | null;
  onClose: () => void;
}) => {
  const open = useStepDialog(!!example, onClose);
  const href = example?.href;
  const downloadHref = example?.downloadHref ?? href;

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      {/* Close to full screen: the point of opening an example is to read it, and a
          document squeezed into a dialog-sized box is not reading. The grid rows keep the
          header fixed and hand every remaining pixel to the document. */}
      <DialogContent className="h-[90vh] max-h-none w-[94vw] max-w-[94vw] grid-rows-[auto_1fr] overflow-hidden sm:max-w-[1500px]">
        <DialogDescription className="sr-only">
          An example of the kind of document Volca needs.
        </DialogDescription>
        <DialogHeader>
          <Stack
            direction="row"
            gap={6}
            align="center"
            justify="between"
            className="w-full pr-10"
          >
            <DialogTitle>{example?.label}</DialogTitle>
            {downloadHref && (
              <a
                href={downloadHref}
                download
                className="shrink-0 text-xs text-link underline-offset-4 hover:underline"
              >
                {/* The Word file when there is one: a plan is something they edit, and a
                    rendered copy is not editable. */}
                {downloadHref.endsWith(".docx")
                  ? "Download the Word version"
                  : "Download this example"}
              </a>
            )}
          </Stack>
        </DialogHeader>

        <Stack
          gap={4}
          align="stretch"
          className="min-h-0 w-full overflow-hidden"
        >
          {href && (
            <iframe
              // navpanes=0 opens on the page itself rather than behind a strip of
              // thumbnails, which is most of the width gone before anything is read.
              src={`${href}#view=FitH&navpanes=0&pagemode=none`}
              title={example?.label ?? "Example document"}
              className="min-h-0 w-full flex-1 rounded-md border border-line"
            />
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
